/**
 * frontend/src/core/tipos/tiposConsumoTokens.ts
 *
 * Tipos TypeScript para sistema de rastreamento de consumo de tokens LLM.
 *
 * CONTEXTO:
 * Estes tipos descrevem os contratos trocados entre frontend e backend
 * para consulta de consumo de tokens por usuário e por organização.
 *
 * PADRÕES:
 * - Nomes longos e descritivos em português
 * - Timestamps vindos da API são strings ISO-8601
 */

/**
 * Consumo de tokens agregado por usuário.
 */
export interface ConsumoTokensUsuario {
  /** ID do usuário */
  usuario_id: string;
  /** Email do usuário */
  email: string | null;
  /** Nome do usuário */
  nome: string | null;
  /** ID da organização primária (se aplicável) */
  organizacao_id: string | null;
  /** Nome da organização */
  nome_organizacao: string | null;
  /** Total de tokens consumidos */
  total_tokens: number;
  /** Total de tokens de prompt */
  total_tokens_prompt: number;
  /** Total de tokens de resposta */
  total_tokens_resposta: number;
  /** Custo total estimado em USD */
  custo_total_usd: number;
  /** Total de chamadas ao LLM */
  total_chamadas: number;
  /** Chamadas bem sucedidas */
  chamadas_sucesso: number;
  /** Chamadas com erro */
  chamadas_erro: number;
  /** Timestamp da última chamada */
  ultima_chamada: string | null;
}

/**
 * Consumo de tokens agregado por organização.
 */
export interface ConsumoTokensOrganizacao {
  /** ID da organização */
  organizacao_id: string;
  /** Nome da organização */
  nome_organizacao: string | null;
  /** Total de tokens consumidos */
  total_tokens: number;
  /** Total de tokens de prompt */
  total_tokens_prompt: number;
  /** Total de tokens de resposta */
  total_tokens_resposta: number;
  /** Custo total estimado em USD */
  custo_total_usd: number;
  /** Total de chamadas ao LLM */
  total_chamadas: number;
  /** Total de membros na organização */
  total_membros: number;
  /** Membros que fizeram chamadas */
  membros_com_consumo: number;
  /** Breakdown por usuário */
  consumo_por_usuario: ConsumoTokensUsuario[];
}

/**
 * Consumo por modelo LLM.
 */
export interface ConsumoModelo {
  /** Total de tokens usados neste modelo */
  total_tokens: number;
  /** Custo total em USD */
  custo_total_usd: number;
  /** Total de chamadas */
  total_chamadas: number;
}

/**
 * Resumo geral de consumo para dashboard admin.
 */
export interface ResumoConsumoTokens {
  /** Total de tokens consumidos globalmente */
  total_tokens_global: number;
  /** Custo total global em USD */
  custo_total_global_usd: number;
  /** Total de chamadas ao LLM */
  total_chamadas_global: number;
  /** Usuários que fizeram chamadas */
  total_usuarios_com_consumo: number;
  /** Organizações com consumo */
  total_organizacoes_com_consumo: number;
  /** Top usuários por consumo */
  top_usuarios: ConsumoTokensUsuario[];
  /** Top organizações por consumo */
  top_organizacoes: ConsumoTokensOrganizacao[];
  /** Consumo por modelo LLM */
  consumo_por_modelo: Record<string, ConsumoModelo>;
  /** Início do período */
  periodo_inicio: string | null;
  /** Fim do período */
  periodo_fim: string | null;
  /** Timestamp do relatório */
  timestamp: string;
}

/**
 * Parâmetros para filtrar consumo de tokens.
 */
export interface FiltroConsumoTokens {
  /** Últimos N dias */
  dias?: number;
  /** Data de início do período */
  data_inicio?: string;
  /** Data de fim do período */
  data_fim?: string;
  /** Limite de resultados */
  limite?: number;
  /** Ordenar por: tokens ou custo */
  ordem?: 'tokens' | 'custo';
  /** Top N resultados */
  top_n?: number;
  /** Incluir detalhes por usuário (para org) */
  incluir_detalhes_usuarios?: boolean;
}

/**
 * Formata o consumo de tokens para exibição.
 */
export function formatarTokens(tokens: number): string {
  if (tokens >= 1000000) {
    return `${(tokens / 1000000).toFixed(2)}M`;
  }
  if (tokens >= 1000) {
    return `${(tokens / 1000).toFixed(1)}K`;
  }
  return tokens.toString();
}

/**
 * Formata o custo em USD para exibição.
 */
export function formatarCustoUSD(custo: number): string {
  if (custo < 0.01) {
    return `$${custo.toFixed(4)}`;
  }
  if (custo < 1) {
    return `$${custo.toFixed(3)}`;
  }
  return `$${custo.toFixed(2)}`;
}

/**
 * Calcula a taxa de erro de chamadas.
 */
export function calcularTaxaErro(consumo: ConsumoTokensUsuario): number {
  if (consumo.total_chamadas === 0) return 0;
  return (consumo.chamadas_erro / consumo.total_chamadas) * 100;
}
